import { useEffect, useState } from "react";
import MenuIfoodDB from "../database/wrappers/menuIfood";

import type {
    CategoryIfood,
    ComplementCategoryIfood,
    ComplementIfood,
    ItemIfood,
    ProductIfood,
} from "../database/entities/stores/menuIfood";

type Dict<T> = Record<string, T>;
type Unsubscribe = () => void;

function toDict<T extends { id: string }>(arr: T[]): Dict<T> {
    return Object.fromEntries(arr.map((x) => [x.id, x])) as Dict<T>;
}

type MenuSchema = {
    categories: CategoryIfood;
    products: ProductIfood;
    complementCategories: ComplementCategoryIfood;
    complements: ComplementIfood;
    items: ItemIfood;
};

type MenuEntity = keyof MenuSchema;

type UseIfoodMenuParams = {
    estabSelected?: string | null;
    storeSelected?: string | null;
    ifood_id?: string | null,
    ifood_catalog?: string | null
};

export function useIfoodMenu({ estabSelected, storeSelected, ifood_id, ifood_catalog }: UseIfoodMenuParams) {
    const [ifoodCategories, setCategories] = useState<Dict<CategoryIfood>>({});
    const [ifoodProducts, setProducts] = useState<Dict<ProductIfood>>({});
    const [ifoodComplementsCategories, setComplementCategories] = useState<Dict<ComplementCategoryIfood>>({});
    const [ifoodComplements, setComplements] = useState<Dict<ComplementIfood>>({});
    const [ifoodItems, setItems] = useState<Dict<ItemIfood>>({});

    useEffect(() => {
        if (!estabSelected || !storeSelected || !ifood_id || !ifood_catalog) {
            setCategories({});
            setProducts({});
            setComplementCategories({});
            setComplements({});
            setItems({});
            return;
        }

        const subscribe = <K extends MenuEntity>(
            entity: K,
            set: React.Dispatch<React.SetStateAction<Dict<MenuSchema[K]>>>
        ): Unsubscribe => {
            return new MenuIfoodDB(estabSelected, storeSelected, entity).on<MenuSchema[K]>(
                (arr) => set(toDict(arr))
            );
        };

        const unsubs: Unsubscribe[] = [
            subscribe("categories", setCategories),
            subscribe("products", setProducts),
            subscribe("complementCategories", setComplementCategories),
            subscribe("complements", setComplements),
            subscribe("items", setItems),
        ];

        return () => {
            unsubs.forEach((u) => u());
        };
    }, [estabSelected, storeSelected, ifood_id, ifood_catalog]);

    return {
        ifoodCategories,
        ifoodProducts,
        ifoodComplementsCategories,
        ifoodComplements,
        ifoodItems,
    };
}